import React from 'react';

/**
 * FormGroup - Reusable form field wrapper
 * @param {string} label - Field label (without asterisk - use required prop instead)
 * @param {boolean} required - If true, shows red asterisk inside the field
 * @param {string} htmlFor - ID of the form element
 * @param {string} hint - Helper text below the field
 * @param {string} error - Error message
 * @param {React.ReactNode} children - Form element(s)
 */
export function FormGroup({ label, required, htmlFor, hint, error, children, className = '' }) {
  // Remove asterisk from label if it was accidentally included
  const cleanLabel = label ? label.replace(/\s*\*\s*$/, '').replace(/\s*\*\s*/, ' ').trim() : '';
  
  return (
    <div className={`form-group ${className}`}>
      {cleanLabel && <label htmlFor={htmlFor}>{cleanLabel}</label>}
      {required ? (
        <div className="input-with-indicator">
          {children}
          <span className="required-indicator">*</span>
        </div>
      ) : (
        children
      )}
      {hint && <small className="form-hint">{hint}</small>}
      {error && <small className="form-error-text">{error}</small>}
    </div>
  );
}

/**
 * FormRow - Horizontal form layout
 */
export function FormRow({ children, className = '' }) {
  return (
    <div className={`form-row ${className}`}>
      {children}
    </div>
  );
}

/**
 * ToggleSwitch - Reusable toggle component
 */
export function ToggleSwitch({ checked, onChange, disabled = false, label }) {
  return (
    <label className="toggle-switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span className={`toggle-slider ${disabled ? 'disabled' : ''}`}></span>
      {label && <span className="toggle-label">{label}</span>}
    </label>
  );
}

/**
 * ToggleItem - Toggle with label and description
 */
export function ToggleItem({ checked, onChange, disabled, label, description }) {
  return (
    <div className="toggle-item">
      <div className="toggle-info">
        <label className="toggle-label">{label}</label>
        {description && <p className="toggle-description">{description}</p>}
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

/**
 * InfoBox - Styled information box
 */
export function InfoBox({ title, children, className = '' }) {
  return (
    <div className={`settings-info-box ${className}`}>
      {title && <h4>{title}</h4>}
      {children}
    </div>
  );
}

/**
 * WarningBox - Styled warning box
 */
export function WarningBox({ title, children, className = '' }) {
  return (
    <div className={`warning-box ${className}`}>
      {title && <strong>{title}</strong>}
      {children}
    </div>
  );
}
